import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import styled from '@emotion/styled';

import { Flex } from 'common/components/Flex';
import { HeadlineH3, SubtitleST4 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import { RESULTS_HEADING_ID } from '../constants';
import { RankingInfo } from './RankingInfo';

export interface ResultsHeadingProps {
  headline: string;
  showQuotes?: boolean;
  count?: number;
  forceCountNewLine?: boolean;
}

const Container = Flex;

const containerStyles: ComponentProps<typeof Container> = {
  as: 'header',
  width: ['100%', 'auto'],
  mr: ['sp_0', null, 'sp_32', 'sp_48'],
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: ['sp_8', null, 'sp_12'],
  justifyContent: ['space-between', 'flex-start', 'flex-start', 'flex-start'],
};

const HeadlineContainer = styled(Flex)``;

const headlineContainerStyles: ComponentProps<typeof HeadlineContainer> = {
  flex: '1 1 auto',
  minWidth: 0,
  mr: 'sp_8',
  mb: 'sp_0',
};

const CountSectionContainer = styled(Flex)``;

const ResultsHeading = ({
  children,
  showQuotes = false,
  forceCountNewLine = false,
  ...props
}: PropsWithChildren<ResultsHeadingProps>) => {
  const { r } = useLocalization();

  const headline = showQuotes
    ? r.t('h24_search_page_results_heading', { searchQuery: props.headline })
    : props.headline;
  const count = r.t('h24_results_count_heading', { count: props.count ? props.count : '0' });

  const countSectionContainerStyles: ComponentProps<typeof CountSectionContainer> = {
    flex: forceCountNewLine ? ['1 1 100%', '0 0 auto'] : '0 0 auto',
    justifyContent: children ? 'space-between' : 'flex-start',
  };

  return (
    <Container
      {...containerStyles}
      data-corgi-id={RESULTS_HEADING_ID}
      role="group"
      aria-label={`${headline} ${count}`}
    >
      <HeadlineContainer {...headlineContainerStyles}>
        <HeadlineH3 as="h1">{headline}</HeadlineH3>
      </HeadlineContainer>
      <CountSectionContainer alignItems="center" my="-8px" {...countSectionContainerStyles}>
        <Flex alignItems="center">
          <SubtitleST4 as="small" color="sys.neutral.text.default">
            {count}
          </SubtitleST4>
          <RankingInfo />
        </Flex>
        {children}
      </CountSectionContainer>
    </Container>
  );
};

export default ResultsHeading;
